import * as React from 'react'
import { Command } from 'cmdk'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ArrowRight, FileText, Languages, Loader2, Moon, PawPrint, Phone, Plus, Search, Sun, UserRound, Zap, ListChecks, GitBranch } from 'lucide-react'
import { useMe } from '@/api/auth'
import { useManifest, type ActionManifest } from '@/api/hooks'
import { useGlobalSearch, hitHref, type SearchHit } from '@/api/search'
import { EXTRA_ROUTES, visibleNav, canSee } from './nav'
import { useI18n, type TKey } from '@/i18n'
import { useTheme } from '@/lib/theme'
import { openCreateTask, ui, useUI } from '@/lib/store'
import { useGo } from '@/lib/routing'
import { Kbd } from '@/components/ui/misc'

/** Where to go to perform an action from the manifest (UI parity for the palette). */
export function routeForAction(id: string): string {
  const [domain] = id.split('.')
  const map: Record<string, string> = {
    tasks: '/tasks',
    users: '/admin/users',
    tokens: '/profile?tab=tokens',
    auth: '/profile',
    prefs: '/profile?tab=notifications',
    audit: '/admin/audit',
    connectors: '/admin/connectors',
    sync: '/admin/sync',
    jobs: '/admin/jobs',
    settings: '/admin/settings',
    system: '/admin/system',
    schedule: '/schedule',
    journeys: '/journeys',
    clients: '/clients',
    pets: '/clients',
    labs: '/doctor',
    assistant: '/doctor',
    recommendations: '/doctor',
    visits: '/journeys',
    prescriptions: '/clients',
    vaccinations: '/clients',
    directory: '/schedule',
    search: '/clients',
    analytics: '/',
    segments: '/marketing',
    campaigns: '/marketing',
    kiosk: '/admin/client-app',
    room_sessions: '/admin/rooms',
    transcripts: '/admin/rooms',
    comms: '/admin/system',
    enote: '/admin/system',
    oauth: '/profile?tab=tokens',
    calls: '/calls',
    inbox: '/inbox',
    finance: '/finance',
    ratings: '/quality',
    insights: '/insights',
    kpi: '/',
    rooms: '/admin/rooms',
    cameras: '/admin/rooms',
    operators: '/admin/operators',
    clientapp: '/admin/client-app',
    notifications: '/',
  }
  if (id === 'users.invite') return '/admin/users?invite=1'
  return map[domain] ?? '/'
}

const HIT_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  client: UserRound,
  pet: PawPrint,
  call: Phone,
  task: ListChecks,
  visit: GitBranch,
  journey: GitBranch,
}

const TYPE_LABEL: Record<string, TKey> = {
  client: 'search.type.client',
  pet: 'search.type.pet',
  call: 'search.type.call',
  task: 'search.type.task',
  visit: 'search.type.visit',
  journey: 'search.type.visit',
  appointment: 'search.type.appointment',
}

export function CommandPalette() {
  const open = useUI((s) => s.paletteOpen)
  const setOpen = (v: boolean) => ui.set({ paletteOpen: v })
  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-[640px] -translate-x-1/2 overflow-hidden rounded-[14px] border border-border bg-surface shadow-pop"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          {open && <PaletteBody onClose={() => setOpen(false)} />}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = React.useState(v)
  React.useEffect(() => {
    const id = setTimeout(() => setD(v), ms)
    return () => clearTimeout(id)
  }, [v, ms])
  return d
}

function PaletteBody({ onClose }: { onClose: () => void }) {
  const { t, lang, setLang } = useI18n()
  const { theme, setTheme } = useTheme()
  const me = useMe().data
  const go = useGo()
  const manifest = useManifest().data ?? []
  const [value, setValue] = React.useState('')
  const term = useDebounced(value, 180)
  const search = useGlobalSearch(term)

  const run = (fn: () => void) => {
    onClose()
    fn()
  }
  const nav = [...visibleNav(me).flatMap((s) => s.items), ...EXTRA_ROUTES.filter((r) => canSee(r, me))]
  const seen = new Set<string>()
  const navItems = nav.filter((n) => (seen.has(n.to) ? false : (seen.add(n.to), true)))

  const groups = new Map<string, ActionManifest[]>()
  for (const a of manifest) {
    const g = groups.get(a.group) ?? []
    g.push(a)
    groups.set(a.group, g)
  }

  const itemCls =
    'flex cursor-default items-center gap-3 rounded-[8px] px-3 py-2 text-[13.5px] text-ink data-[selected=true]:bg-brand-soft data-[selected=true]:text-brand-soft-ink [&_svg]:size-4 [&_svg]:text-muted'
  const groupCls = '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-muted'

  return (
    <Command label={t('search.open')} loop className="flex max-h-[70vh] flex-col">
      <div className="flex items-center gap-2 border-b border-border px-4">
        <Search className="size-4 text-muted" aria-hidden />
        <Command.Input
          autoFocus
          value={value}
          onValueChange={setValue}
          placeholder={t('search.paletteHint')}
          className="h-12 w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-muted"
        />
        {search.isFetching && <Loader2 className="size-4 animate-spin text-muted" aria-hidden />}
        <Kbd>Esc</Kbd>
      </div>
      <Command.List className="overflow-y-auto p-2 scrollbar-thin">
        <Command.Empty className="px-3 py-8 text-center text-[13px] text-muted">{t('search.nothing')}</Command.Empty>

        {term.trim().length >= 2 && search.availability === 'ok' && search.hits.length > 0 && (
          <Command.Group heading={t('search.results')} className={groupCls} forceMount>
            {search.hits.map((h: SearchHit) => {
              const Icon = HIT_ICON[h.type] ?? FileText
              return (
                <Command.Item key={`${h.type}:${h.id}`} value={`hit ${h.type} ${h.id} ${h.title}`} forceMount className={itemCls} onSelect={() => run(() => go(hitHref(h)))}>
                  <Icon />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{h.title}</span>
                    {h.subtitle && <span className="truncate text-[12px] text-muted">{h.subtitle}</span>}
                  </span>
                  <span className="ml-auto text-[11px] text-muted">{TYPE_LABEL[h.type] ? t(TYPE_LABEL[h.type]) : h.type}</span>
                </Command.Item>
              )
            })}
          </Command.Group>
        )}
        {term.trim().length >= 2 && search.availability === 'missing' && (
          <div className="mx-3 my-2 rounded-[8px] bg-sunken px-3 py-2 text-[12px] text-muted">{t('search.moduleMissing')}</div>
        )}

        <Command.Group heading={t('search.quick')} className={groupCls}>
          {manifest.some((a) => a.id === 'tasks.create') && (
            <Command.Item value="create task нове завдання" className={itemCls} onSelect={() => run(() => openCreateTask())} data-action-id="tasks.create">
              <Plus /> {t('tasks.new')}
              <Kbd className="ml-auto">C</Kbd>
            </Command.Item>
          )}
          <Command.Item value="theme тема dark light" className={itemCls} onSelect={() => run(() => setTheme(theme === 'dark' ? 'light' : 'dark'))}>
            {theme === 'dark' ? <Sun /> : <Moon />} {t('search.toggleTheme')}
          </Command.Item>
          <Command.Item value="language мова english українська" className={itemCls} onSelect={() => run(() => setLang(lang === 'uk' ? 'en' : 'uk'))}>
            <Languages /> {lang === 'uk' ? 'Switch to English' : 'Українською'}
          </Command.Item>
        </Command.Group>

        <Command.Group heading={t('search.navigation')} className={groupCls}>
          {navItems.map((n) => (
            <Command.Item key={n.to} value={`go ${t(n.label)} ${n.to}`} className={itemCls} onSelect={() => run(() => go(n.to))}>
              <n.icon />
              {t(n.label)}
              {n.key && <span className="ml-auto text-[11px] text-muted">G {n.key.toUpperCase()}</span>}
            </Command.Item>
          ))}
        </Command.Group>

        {[...groups.entries()].map(([g, list]) => (
          <Command.Group key={g} heading={`${t('search.actions')} · ${g}`} className={groupCls}>
            {list.map((a) => (
              <Command.Item
                key={a.id}
                value={`action ${a.id} ${a.title} ${a.description}`}
                className={itemCls}
                data-action-id={a.id}
                onSelect={() => run(() => (a.id === 'tasks.create' ? openCreateTask() : go(routeForAction(a.id))))}
              >
                <Zap />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{a.title}</span>
                  <span className="truncate text-[11.5px] text-muted">{a.id}</span>
                </span>
                <ArrowRight className="ml-auto" />
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command>
  )
}
